// The 4 main metric-grid sections of the report body. Pulled out of
// ReportPage.jsx so the page reads as a layout file rather than a 1500-line
// component. Each grid is data-only — all formatting, color logic, and
// info-tooltip wiring lives inside the Metric card itself.

import Metric from "./Metric";
import CapitalStackCard from "./CapitalStackCard";
import LiquidityCard from "./LiquidityCard";
import { thresholdState } from "./theme";
import { fmtCurrency, fmtPct, fmtRatio, fmtNumber, fmtDays } from "../../lib/format";

export function ProfitabilityGrid({ m, currency }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
      data-testid="profitability-grid"
    >
      <Metric
        label="EBITDA"
        value={fmtCurrency(m.ebitda, currency)}
        sub={`${fmtPct(m.ebitda_margin)} margin`}
        state={thresholdState(m.ebitda_margin, [0, 5, 15])}
      />
      <Metric
        label="Gross Margin"
        value={fmtPct(m.gross_margin)}
        sub="Direct profitability"
        state={thresholdState(m.gross_margin, [10, 25, 40])}
      />
      <Metric
        label="Operating Margin"
        value={fmtPct(m.operating_margin)}
        sub="After OpEx & D&A"
        state={thresholdState(m.operating_margin, [0, 5, 12])}
      />
      <Metric
        label="Net Margin"
        value={fmtPct(m.net_margin)}
        sub="After everything"
        state={thresholdState(m.net_margin, [0, 3, 8])}
      />
      <Metric
        label="Operating Income"
        value={fmtCurrency(m.operating_income, currency)}
        sub="EBIT excl. other"
        state="neutral"
      />
      <Metric
        label="EBIT"
        value={fmtCurrency(m.ebit, currency)}
        sub="Earnings before interest & tax"
        state="neutral"
      />
      <Metric
        label="ROA"
        value={fmtPct(m.roa)}
        sub="Return on assets"
        state={thresholdState(m.roa, [0, 3, 8])}
      />
      <Metric
        label="ROE"
        value={fmtPct(m.roe)}
        sub="Return on equity"
        state={thresholdState(m.roe, [0, 8, 15])}
      />
    </div>
  );
}

export function DebtSolvencyGrid({ m, agg, currency }) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-12 gap-6"
      data-testid="debt-solvency-grid"
    >
      <div className="lg:col-span-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Metric
          label="DSCR"
          value={fmtRatio(m.dscr)}
          sub="Lender benchmark ≥ 1.25x"
          state={thresholdState(m.dscr, [1.0, 1.25, 1.5])}
        />
        <Metric
          label="Interest Coverage"
          value={fmtRatio(m.interest_coverage)}
          sub="EBIT / interest"
          state={thresholdState(m.interest_coverage, [1.5, 3, 6])}
        />
        <Metric
          label="Debt / EBITDA"
          value={fmtRatio(m.debt_to_ebitda)}
          sub="Years of EBITDA to repay"
          state={thresholdState(m.debt_to_ebitda, [6, 4, 2.5], false)}
        />
        <Metric
          label="Debt / Equity"
          value={fmtRatio(m.debt_to_equity)}
          sub="Leverage on equity"
          state={thresholdState(m.debt_to_equity, [3, 2, 1], false)}
        />
        <Metric
          label="Debt / Assets"
          value={fmtRatio(m.debt_to_assets)}
          sub="Share of assets funded by debt"
          state={thresholdState(m.debt_to_assets, [0.7, 0.5, 0.3], false)}
        />
        <Metric
          label="Equity Ratio"
          value={fmtRatio(m.equity_ratio)}
          sub="Owner-funded portion"
          state={thresholdState(m.equity_ratio, [0.2, 0.4, 0.6])}
        />
      </div>
      <CapitalStackCard agg={agg} currency={currency} />
    </div>
  );
}

export function LiquidityGrid({ m, currency }) {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-12 gap-6"
      data-testid="liquidity-grid"
    >
      <LiquidityCard m={m} />
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
        <Metric
          label="Current Ratio"
          value={fmtNumber(m.current_ratio)}
          sub="≥ 1.5 comfortable"
          state={thresholdState(m.current_ratio, [1.0, 1.2, 1.5])}
        />
        <Metric
          label="Quick Ratio"
          value={fmtNumber(m.quick_ratio)}
          sub="≥ 1.0 comfortable"
          state={thresholdState(m.quick_ratio, [0.5, 0.8, 1.0])}
        />
        <Metric
          label="Cash Ratio"
          value={fmtNumber(m.cash_ratio)}
          sub="Cash / current liabilities"
          state={thresholdState(m.cash_ratio, [0.1, 0.3, 0.5])}
        />
        <Metric
          label="Working Capital"
          value={fmtCurrency(m.working_capital, currency)}
          sub="Current assets − liabilities"
          state={thresholdState(m.working_capital, [0, 1, 1])}
        />
      </div>
    </div>
  );
}

export function EfficiencyGrid({ m, currency }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5"
      data-testid="efficiency-grid"
    >
      <Metric
        label="Asset Turnover"
        value={fmtRatio(m.asset_turnover)}
        sub="Revenue per $ of assets"
        state={thresholdState(m.asset_turnover, [0.4, 0.8, 1.5])}
      />
      <Metric
        label="Inventory Turnover"
        value={fmtRatio(m.inventory_turnover)}
        sub="Times sold per year"
        state={thresholdState(m.inventory_turnover, [2, 5, 10])}
      />
      <Metric
        label="DSO"
        value={fmtDays(m.dso)}
        sub="Days sales outstanding"
        state={thresholdState(m.dso, [60, 45, 30], false)}
      />
      <Metric
        label="DIO"
        value={fmtDays(m.dio)}
        sub="Days inventory outstanding"
        state={thresholdState(m.dio, [120, 75, 45], false)}
      />
      <Metric
        label="DPO"
        value={fmtDays(m.dpo)}
        sub="Days payables outstanding"
        state="neutral"
      />
      <Metric
        label="Cash Conv. Cycle"
        value={fmtDays(m.cash_conversion_cycle)}
        sub="DSO + DIO − DPO"
        state={thresholdState(m.cash_conversion_cycle, [120, 90, 45], false)}
      />
      <Metric
        label="OCF Margin"
        value={fmtPct(m.ocf_margin)}
        sub="Cash from ops / revenue"
        state={thresholdState(m.ocf_margin, [0, 5, 12])}
      />
      <Metric
        label="Free Cash Flow"
        value={fmtCurrency(m.free_cash_flow, currency)}
        sub={`${fmtPct(m.fcf_margin)} of revenue`}
        state={thresholdState(m.fcf_margin, [-5, 0, 5])}
      />
    </div>
  );
}
